import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Stack } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { translate as t } from 'hooks/useTranslations';
import { useTemplate } from '../../_shared/_data_sources/useTemplate';
import { filterActiveSections } from '../../_shared/_utils/templateFilters';
import TemplateStatusChip from '../../_shared/TemplateBuilder/components/TemplateStatusChip';
import SectionNavigation from './components/SectionNavigation';
import TemplateSectionPreview from './components/TemplateSectionPreview';
import { getCorrectNavigationUrl } from '../../_shared/TemplateBuilder/_hooks/useNavigationHandlers';

const TemplateViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const templateId = id ? parseInt(id) : 0;
  const confirm = useConfirm();

  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const sectionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const {
    data: template,
    isLoading,
    isError,
    error,
  } = useTemplate(
    templateId,
    {
      enabled: !!templateId,
    },
    (error) => {
      console.error('Error fetching template:', error);
    },
    false
  );

  const activeSections = useMemo(() => {
    if (!template) return [];
    return filterActiveSections(template.templateSections || []);
  }, [template]);

  useEffect(() => {
    if (activeSections.length > 0 && selectedSectionId === null) {
      setSelectedSectionId(activeSections[0].id);
    }
  }, [activeSections]);

  useEffect(() => {
    if (selectedSectionId && sectionRefs.current[selectedSectionId]) {
      sectionRefs.current[selectedSectionId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [selectedSectionId]);

  const handleBackClick = () => {
    navigate('../');
  };

  const handleEditClick = async () => {
    try {
      await confirm({
        title: t('performanceReporting.templates.viewer.modal.title'),
        description: t('performanceReporting.templates.viewer.modal.body'),
        confirmationText: t('performanceReporting.templates.viewer.modal.continue'),
        cancellationText: t('performanceReporting.templates.viewer.modal.cancel'),
        confirmationButtonProps: { variant: 'contained', color: 'primary' },
        cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      });
      // User confirmed, navigate to draft
      navigate(`../builder/${templateId}`);
    } catch {
      // User cancelled, do nothing
    }
  };

  const properBackNavigationUrl = template ? getCorrectNavigationUrl(template) : '../';

  // Show error state
  if (isError) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.templates.builder.error.loadingTitle')}
        backUrl={properBackNavigationUrl}
        onBackClick={handleBackClick}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            gap: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant='h5'
            color='error.main'
          >
            {t('performanceReporting.templates.builder.error.failedToLoad')}
          </Typography>
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ maxWidth: 400 }}
          >
            {error instanceof Error ? error.message : t('performanceReporting.templates.builder.error.loadingError')}
          </Typography>
        </Box>
      </TakeoverLayout>
    );
  }

  // Show loading state
  if (isLoading || !template) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.templates.builder.loading.title')}
        backUrl={properBackNavigationUrl}
        onBackClick={handleBackClick}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </TakeoverLayout>
    );
  }

  return (
    <TakeoverLayout
      pageTitle={template.name}
      backUrl={properBackNavigationUrl}
      onBackClick={handleBackClick}
      contentHeaderLeft={
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
        >
          <TemplateStatusChip status={template.status || 'Active'} />
        </Stack>
      }
      contentHeaderRight={
        <Button
          variant='outlined'
          color='primary'
          size='small'
          onClick={handleEditClick}
        >
          {t('performanceReporting.templates.viewer.editButton')}
        </Button>
      }
      leftDrawer={{
        header: (
          <Typography
            variant='h6'
            sx={{ fontWeight: 600 }}
          >
            {t('performanceReporting.templates.viewer.sections')}
          </Typography>
        ),
        content: (
          <SectionNavigation
            sections={template.templateSections}
            selectedSectionId={selectedSectionId}
            onSectionSelect={setSelectedSectionId}
          />
        ),
        closeable: true,
      }}
      contentBackground='neutral'
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '900px',
          mx: 'auto',
          py: 3,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {activeSections.map((section) => (
          <Box
            key={section.id}
            ref={(el: HTMLDivElement | null) => {
              sectionRefs.current[section.id] = el;
            }}
          >
            <TemplateSectionPreview section={section} />
          </Box>
        ))}
      </Box>
    </TakeoverLayout>
  );
};

export default TemplateViewer;
